import { Accordion, AccordionDetails, AccordionSummary, Divider, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CustomFieldDto, CustomFieldOptionDto, CustomFieldType, ReportParametersInput } from 'gql';
import { useFormContext, useWatch } from 'react-hook-form';
import { useReportSystemFields } from '../../hooks/useReportSystemFields';

type CustomField = Pick<CustomFieldDto, 'id' | 'name' | 'customFieldType' | 'systemFieldId'> & {
  options: Pick<CustomFieldOptionDto, 'id' | 'name'>[];
};

interface OwnProps {
  activityFields: CustomField[];
  individualFields: CustomField[];
  unknownGroupFields: CustomField[];
  realityFields: CustomField[];
  disabled?: boolean;
}

export type ReportFieldsVisibilityProps = OwnProps;

const ReportFieldsVisibility: React.FC<ReportFieldsVisibilityProps> = (props) => {
  const { formatMessage } = useIntl();
  const { disabled, activityFields, individualFields, unknownGroupFields, realityFields } = props;
  const { control, setValue } = useFormContext<ReportParametersInput>();
  const fieldsVisibility = useWatch({ control, name: 'fieldsVisibility' });
  const { activityTypeField, themeField, specificationField, distributionField } = useReportSystemFields();

  // Filter to only show Selection fields (non-Selection fields are excluded from PDF reports)
  const filterReportableFields = (fields: CustomField[]) =>
    fields.filter(field => field.customFieldType === CustomFieldType.Selection);

  const activityVisibilityFields = useMemo<CustomField[]>(() => ([
    activityTypeField,
    ...filterReportableFields(activityFields)
  ]), [activityFields, activityTypeField]);

  const realitiesVisibilityFields = useMemo<CustomField[]>(() => ([
    themeField,
    specificationField,
    ...filterReportableFields(realityFields)
  ]), [realityFields, specificationField, themeField]);

  const distributionVisibilityFields = useMemo<CustomField[]>(() => ([
    distributionField
  ]), [distributionField]);

  const filteredIndividualFields = useMemo<CustomField[]>(() =>
    filterReportableFields(individualFields), [individualFields]);

  const filteredUnknownGroupFields = useMemo<CustomField[]>(() =>
    filterReportableFields(unknownGroupFields), [unknownGroupFields]);

  const allCustomFieldIds = useMemo(() => ([
    ...filterReportableFields(activityFields).map(f => f.id),
    ...filteredIndividualFields.map(f => f.id),
    ...filteredUnknownGroupFields.map(f => f.id),
    ...filterReportableFields(realityFields).map(f => f.id),
  ]), [activityFields, filteredIndividualFields, filteredUnknownGroupFields, realityFields]);

  const isCustomFieldVisible = (id: number) => {
    const current = fieldsVisibility?.visibleCustomFieldIds;
    if (current == null) return true;
    return current.includes(id);
  };

  const isFieldVisible = (field: CustomField) => {
    if (field.id === activityTypeField.id) return fieldsVisibility?.areActivityTypesVisible ?? true;
    if (field.id === themeField.id) return fieldsVisibility?.areThemesVisible ?? true;
    if (field.id === specificationField.id) return fieldsVisibility?.areSpecificationsVisible ?? true;
    if (field.id === distributionField.id) return fieldsVisibility?.areDistributionsVisible ?? true;
    return isCustomFieldVisible(field.id);
  };

  const toggleField = (fieldId: number) => {
    if (fieldId === activityTypeField.id) {
      setValue('fieldsVisibility.areActivityTypesVisible', !(fieldsVisibility?.areActivityTypesVisible ?? true), { shouldDirty: true });
      return;
    }
    if (fieldId === themeField.id) {
      setValue('fieldsVisibility.areThemesVisible', !(fieldsVisibility?.areThemesVisible ?? true), { shouldDirty: true });
      return;
    }
    if (fieldId === specificationField.id) {
      setValue('fieldsVisibility.areSpecificationsVisible', !(fieldsVisibility?.areSpecificationsVisible ?? true), { shouldDirty: true });
      return;
    }
    if (fieldId === distributionField.id) {
      setValue('fieldsVisibility.areDistributionsVisible', !(fieldsVisibility?.areDistributionsVisible ?? true), { shouldDirty: true });
      return;
    }

    const current = fieldsVisibility?.visibleCustomFieldIds;
    if (current == null) {
      setValue(
        'fieldsVisibility.visibleCustomFieldIds',
        allCustomFieldIds.filter(id => id !== fieldId),
        { shouldDirty: true }
      );
      return;
    }

    const set = new Set<number>(current);
    if (set.has(fieldId)) {
      set.delete(fieldId);
    } else {
      set.add(fieldId);
    }
    setValue('fieldsVisibility.visibleCustomFieldIds', Array.from(set), { shouldDirty: true });
  };

  const renderSection = (titleId: string, fields: CustomField[]) => (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        '&:before': { display: 'none' },
        border: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': {
          borderBottom: 'none'
        }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">{formatMessage({ id: titleId })}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {fields.map(field => (
            <FormControlLabel
              key={field.id}
              control={
                <Switch
                  size="small"
                  checked={isFieldVisible(field)}
                  onChange={() => toggleField(field.id)}
                  disabled={disabled}
                  inputProps={{ 'aria-label': `${field.name} visibility` }}
                />
              }
              label={field.name}
              sx={{ ml: 0 }}
            />
          ))}
          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {formatMessage({ id: 'No fields available' })}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Paper aria-disabled={disabled}>
      <Typography variant="h6" p={2}>{formatMessage({ id: 'Fields visibility' })}</Typography>
      <Divider />
      <Stack p={1} spacing={1}>
        {renderSection('Activities', activityVisibilityFields)}
        {renderSection('Known individuals', filteredIndividualFields)}
        {renderSection('Unknown Individuals', filteredUnknownGroupFields)}
        {renderSection('Realities', realitiesVisibilityFields)}
        {renderSection('Distributions', distributionVisibilityFields)}
      </Stack>
    </Paper>
  );
};

export default ReportFieldsVisibility;
